import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { useMediaQuery, useTheme } from '@mui/material';

import { useModal, useToastr } from '@farmshare/ui-components';
import {
  EnumUserRelationshipsType,
  useCreateProcessorSchedulingMutation,
  useCustomFieldValueBulkSaveMutation,
  useMyProcessorsQuery,
} from '../../../lib/graphql';
import { userState } from 'state';

import { createProducerQuickScheduleMultiStepConfig } from '../components/producer-quick-schedule-steps';
import type {
  ProcessorOption,
  QuickScheduleForm,
} from '../components/quick-schedule';

export function useProducerQuickSchedule() {
  const user = useRecoilValue(userState);
  const navigate = useNavigate();
  const { multiStep } = useModal();
  const { pushToast } = useToastr();
  const [createScheduling] = useCreateProcessorSchedulingMutation();
  const [bulkSaveCustomFieldValues] = useCustomFieldValueBulkSaveMutation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const vendorIds = useMemo(() => {
    const ids = (user?.relationships ?? [])
      .filter((r) => r?.type === EnumUserRelationshipsType.Producer)
      .map((r) => r?.vendorId)
      .filter((v): v is string => Boolean(v));
    return Array.from(new Set(ids));
  }, [user?.relationships]);

  const { data, loading } = useMyProcessorsQuery({
    variables: { vendorIds },
    skip: vendorIds.length === 0,
  });

  const processors: ProcessorOption[] = useMemo(
    () =>
      (data?.processingPartners ?? [])
        .filter((p) => p?.vendor?._id)
        .map((p) => ({
          vendorId: p!.vendor!._id as string,
          shopName: p!.vendor!.shop_name ?? 'Unnamed processor',
        })),
    [data?.processingPartners],
  );

  const open = useCallback(
    async (opts?: {
      initialVendorId?: string;
      initialDate?: string;
      initialFormOverrides?: Partial<QuickScheduleForm>;
      /** Waitlist entry that this booking is fulfilling. Linked on the resulting job. */
      waitlistId?: string;
      /**
       * Set when a processor admin is booking on behalf of a producer (e.g.
       * fulfilling a waitlist entry). The backend uses this to attribute the
       * job to the producer in `requesterContactInformation`, not the admin.
       */
      isOnBehalfOf?: boolean;
    }) => {
      if (!user) {
        pushToast({
          title: 'Sign in to schedule',
          body: 'You’ll need to be signed in to book a job.',
          severity: 'error',
        });
        return;
      }
      // No initial vendor and no existing relationships → nothing to schedule
      // against. (Caller is responsible for not exposing the trigger here.)
      if (!opts?.initialVendorId && processors.length === 0) {
        pushToast({
          title: 'Pick a processor first',
          body: 'Open a processor’s page and start scheduling there.',
          severity: 'warning',
        });
        return;
      }

      // The final "Deposit" step shows/hides itself live based on the chosen
      // processor + species (see `depositApplicable` in the Details step), so
      // there's nothing to resolve at open time.
      const config = createProducerQuickScheduleMultiStepConfig({
        processors,
        user,
        createScheduling,
        bulkSaveCustomFieldValues,
        pushToast,
        onSuccess: (scheduleId) => navigate(`/job/${scheduleId}`),
        initialVendorId: opts?.initialVendorId,
        initialDate: opts?.initialDate,
        initialFormOverrides: opts?.initialFormOverrides,
        waitlistId: opts?.waitlistId,
        isOnBehalfOf: opts?.isOnBehalfOf,
      });

      multiStep({
        ...config,
        fullScreen: isMobile,
        // On mobile, drop the title bar and let the stepped-progress serve as
        // the header. The Cancel button on the footer replaces the close-X.
        ...(isMobile && {
          title: undefined,
          showCancelOnFirstStep: true,
        }),
      });
    },
    [
      user,
      processors,
      createScheduling,
      bulkSaveCustomFieldValues,
      pushToast,
      multiStep,
      navigate,
      isMobile,
    ],
  );

  return {
    open,
    canSchedule: processors.length > 0,
    /**
     * The processor list is still on its way, so `canSchedule` is not an
     * answer yet — false means "don't know", not "has none". A caller that
     * branches on `canSchedule` should hold its trigger until this clears.
     */
    loading,
    processors,
  };
}
